// components/sessions/SessionManager.js - VERSION CORRIGÉE SESSION UNIQUE
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ActivityTracker from './ActivityTracker';
import SessionAlerts from './SessionAlerts';
import { sortirPourAccesSuspendu } from '../../services/api';

const API_URL = '/api';

const getAuthHeaders = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return { 'Content-Type': 'application/json' };
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('❌ Erreur headers JWT:', error);
    return { 'Content-Type': 'application/json' };
  }
};

const SessionManager = () => {
  // États
  const [sessionInfo, setSessionInfo] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [alertLevel, setAlertLevel] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [authMethod, setAuthMethod] = useState(null);

  // Refs
  const timerRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const initializedRef = useRef(false);

  // ✅ CORRECTION : Configuration optimisée
  const config = {
    CHECK_INTERVAL: 30 * 1000,        // ✅ 30s au lieu de 60s
    ACTIVITY_TIMEOUT: 2 * 60 * 1000,
    ALERT_TIMES: {
      WARNING: 3 * 60 * 1000,    // 3 min
      URGENT: 1 * 60 * 1000,     // 1 min
      CRITICAL: 30 * 1000        // 30 sec
    }
  };

  // ✅ CORRECTION : Ordre de vérification corrigé
  const calculateTimeInfo = (sessionData) => {
    // ✅ ORDRE CORRIGÉ : Vérifier d'abord le niveau racine
    let expiresAt = sessionData.expiresAt; // ← Priorité 1
    
    if (!expiresAt && sessionData.session?.expiresAt) { // ← Priorité 2
      expiresAt = sessionData.session.expiresAt;
    }
    
    if (!expiresAt && sessionData.session?.expires) { // ← Priorité 3
      expiresAt = sessionData.session.expires;
    }
    
    if (!expiresAt && sessionData.exp) { // ← Priorité 4 (JWT)
      expiresAt = new Date(sessionData.exp * 1000).toISOString();
    }
    
    // ✅ AJOUT : Log pour debug
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [SessionManager] calculateTimeInfo:', {
        hasExpiresAt: !!expiresAt,
        expiresAt: expiresAt,
        source: sessionData.expiresAt ? 'root' : 
                sessionData.session?.expiresAt ? 'session.expiresAt' : 
                sessionData.exp ? 'exp' : 'none'
      });
    }
    
    if (!expiresAt) {
      console.warn('⚠️ [SessionManager] Aucune date d\'expiration trouvée');
      return {
        timeLeft: null,
        timeLeftMinutes: 0,
        alertLevel: null,
        shouldAlert: false,
        expiresAt: null,
        canExtend: true
      };
    }

    const now = Date.now();
    const expiresAtTime = new Date(expiresAt).getTime();
    const timeLeft = Math.max(0, expiresAtTime - now);

    let alertLevel = null;
    if (timeLeft <= config.ALERT_TIMES.CRITICAL) {
      alertLevel = 'CRITICAL';
    } else if (timeLeft <= config.ALERT_TIMES.URGENT) {
      alertLevel = 'URGENT';
    } else if (timeLeft <= config.ALERT_TIMES.WARNING) {
      alertLevel = 'WARNING';
    }

    return {
      timeLeft,
      timeLeftMinutes: Math.floor(timeLeft / (60 * 1000)),
      alertLevel,
      shouldAlert: alertLevel !== null,
      expiresAt: expiresAt,
      canExtend: true
    };
  };

  const calculateBasicTimeInfo = (jwtData) => {
    const now = Date.now();
    const tokenExp = jwtData.exp ? jwtData.exp * 1000 : (now + 24 * 60 * 60 * 1000);
    const timeLeft = Math.max(0, tokenExp - now);

    return {
      timeLeft,
      timeLeftMinutes: Math.floor(timeLeft / (60 * 1000)),
      alertLevel: null,
      shouldAlert: false,
      expiresAt: new Date(tokenExp).toISOString(),
      canExtend: false
    };
  };

  // ✅ CORRECTION : Ne pas forcer logout si JWT valide
  const checkSessionStatus = useCallback(async () => {
    try {
      // 1️⃣ Essayer d'abord la session MySQL
      const sessionResponse = await fetch(`${API_URL}/auth/verify`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      // Accès suspendu pendant la session : on sort tout de suite, sans tenter
      // le jeton — il porte le même compte.
      if (!sessionResponse.ok) {
        const refus = await sessionResponse.clone().json().catch(() => null);
        if (refus?.code === 'ACCESS_SUSPENDED') {
          sortirPourAccesSuspendu(refus.message);
          return null;
        }
      }

      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json();
        
        if (sessionData.success && sessionData.user) {
          const timeInfo = calculateTimeInfo(sessionData);
          
          setAuthMethod('session');
          setTimeLeft(timeInfo?.timeLeft || null);
          setAlertLevel(timeInfo?.alertLevel || null);
          setIsActive(true);

          const sessionInfoData = {
            user: sessionData.user,
            sessionId: sessionData.sessionId,
            authMethod: 'session',
            hasAdvancedFeatures: true,
            expiresAt: timeInfo.expiresAt, // ✅ Important pour le timer
            ...timeInfo
          };

          setSessionInfo(sessionInfoData);
          return { ...timeInfo, authMethod: 'session' };
        }
      }

      // 2️⃣ Si session MySQL invalide, essayer JWT SANS forcer logout
      console.log('⚠️ [SessionManager] Session MySQL invalide, tentative JWT...');

      const jwtResponse = await fetch(`${API_URL}/auth/verify`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!jwtResponse.ok) {
        const refus = await jwtResponse.clone().json().catch(() => null);
        if (refus?.code === 'ACCESS_SUSPENDED') {
          sortirPourAccesSuspendu(refus.message);
          return null;
        }
      }

      if (jwtResponse.ok) {
        const jwtData = await jwtResponse.json();
        
        if (jwtData.success && jwtData.user) {
          const basicTimeInfo = calculateBasicTimeInfo(jwtData);
          
          setAuthMethod('jwt');
          setTimeLeft(basicTimeInfo?.timeLeft || null);
          setAlertLevel(null);
          setIsActive(true);

          const sessionInfoData = {
            user: jwtData.user,
            sessionId: null,
            authMethod: 'jwt',
            hasAdvancedFeatures: false,
            expiresAt: basicTimeInfo.expiresAt, // ✅ Important pour le timer
            ...basicTimeInfo
          };

          setSessionInfo(sessionInfoData);
          return { ...basicTimeInfo, authMethod: 'jwt' };
        }
      }

      // 3️⃣ Aucune authentification valide - MAINTENANT on déconnecte
      console.error('❌ [SessionManager] Aucune authentification valide (ni session ni JWT)');
      await forceLogout('no_valid_auth');
      return null;

    } catch (error) {
      console.error('❌ [SessionManager] Erreur vérification:', error);
      // Ne pas forcer logout sur erreur réseau
      return null;
    }
  }, []);

  const handleActivity = useCallback((activityType, intensity = 'normal') => {
    const now = Date.now();
    setLastActivity(now);

    if (authMethod === 'session') {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }

      activityTimeoutRef.current = setTimeout(() => {
        if (sessionInfo?.timeLeft <= config.ACTIVITY_TIMEOUT && sessionInfo?.canExtend) {
          setAlertLevel('SUGGESTION');
        }
      }, config.ACTIVITY_TIMEOUT);

      if (['SUGGESTION', 'WARNING', 'URGENT', 'CRITICAL'].includes(alertLevel)) {
        setAlertLevel(null);
      }
    }
  }, [authMethod, sessionInfo, alertLevel, config.ACTIVITY_TIMEOUT]);

  const extendSession = useCallback(async (reason = 'user_request') => {
    try {
      if (authMethod !== 'session') {
        console.warn('⚠️ [SessionManager] Extension non disponible en mode JWT');
        return { 
          success: false, 
          error: 'Extension non disponible en mode JWT' 
        };
      }

      const response = await fetch(`${API_URL}/auth/extend`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ [SessionManager] Session prolongée:', data);
        
        // Recharger les infos de session
        await checkSessionStatus();
        setAlertLevel(null);
        
        return { success: true, data };
      }

      const errorData = await response.json();
      console.error('❌ [SessionManager] Erreur prolongation:', errorData);
      return { success: false, error: errorData.message || 'Erreur serveur' };
      
    } catch (error) {
      console.error('❌ [SessionManager] Erreur prolongation:', error);
      return { success: false, error: error.message };
    }
  }, [authMethod, checkSessionStatus]);

  const forceLogout = useCallback(async (reason = 'session_expired') => {
    console.log(`🔴 [SessionManager] Déconnexion forcée - Raison: ${reason}`);
    
    const currentAuthMethod = authMethod;
    
    // Nettoyage immédiat de l'état
    setIsActive(false);
    setAuthMethod(null);
    setTimeLeft(null);
    setAlertLevel(null);
    setSessionInfo({});
    
    // Nettoyer tous les timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = null;
    }

    // Appel logout serveur
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: currentAuthMethod === 'jwt' ? getAuthHeaders() : { 'Content-Type': 'application/json' }
      });
      console.log('✅ [SessionManager] Logout serveur réussi');
    } catch (error) {
      console.error('❌ [SessionManager] Erreur logout serveur:', error);
    }

    // Nettoyage localStorage
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedInNew');
      console.log('✅ [SessionManager] localStorage nettoyé');
    } catch (storageError) {
      console.warn('⚠️ [SessionManager] Erreur nettoyage storage:', storageError);
    }

    // Redirection
    setTimeout(() => {
      window.location.href = '/connexion';
    }, 500);
  }, [authMethod]);

  // ✅ Initialisation - vérifier session toutes les 30s
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    console.log('🚀 [SessionManager] Initialisation...');
    checkSessionStatus();

    // ✅ Timer vérifie toutes les 30 secondes
    checkIntervalRef.current = setInterval(async () => {
      const timeInfo = await checkSessionStatus();
      
      if (timeInfo && timeInfo.timeLeft === 0) {
        console.log('⏰ [SessionManager] Session expirée détectée par timer');
        await forceLogout('session_expired_timer');
      }
    }, config.CHECK_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
    };
  }, [checkSessionStatus, forceLogout, config.CHECK_INTERVAL]);

  // Vérification inactivité
  useEffect(() => {
    if (!isActive || authMethod !== 'session') return;

    const inactivityChecker = setInterval(() => {
      const now = Date.now();
      const timeSinceLast = now - lastActivity;

      if (
        timeSinceLast >= config.ACTIVITY_TIMEOUT &&
        sessionInfo?.canExtend &&
        timeLeft > 0
      ) {
        setAlertLevel('SUGGESTION');
      }
    }, 60 * 1000);

    return () => clearInterval(inactivityChecker);
  }, [isActive, authMethod, lastActivity, sessionInfo, timeLeft, config.ACTIVITY_TIMEOUT]);

  // ✅ CORRECTION : Timer basé sur expiresAt (pas de décrémentation locale)
  useEffect(() => {
    if (!sessionInfo?.expiresAt || !isActive) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // ✅ Mise à jour toutes les secondes basée sur expiresAt (pas de décrémentation locale)
    timerRef.current = setInterval(() => {
      if (!isActive) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      const now = Date.now();
      const expiresAtTime = new Date(sessionInfo.expiresAt).getTime();
      const newTimeLeft = Math.max(0, expiresAtTime - now);
      
      setTimeLeft(newTimeLeft);
      
      // Mettre à jour alertLevel si nécessaire
      if (authMethod === 'session') {
        let newAlertLevel = null;
        if (newTimeLeft <= config.ALERT_TIMES.CRITICAL) {
          newAlertLevel = 'CRITICAL';
        } else if (newTimeLeft <= config.ALERT_TIMES.URGENT) {
          newAlertLevel = 'URGENT';
        } else if (newTimeLeft <= config.ALERT_TIMES.WARNING) {
          newAlertLevel = 'WARNING';
        }
        
        if (newAlertLevel !== alertLevel) {
          setAlertLevel(newAlertLevel);
        }
      }
      
      // Si expiré, forcer logout
      if (newTimeLeft === 0) {
        console.log('⏰ [SessionManager] Timer local détecte expiration');
        forceLogout('session_expired_client_timer');
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [sessionInfo?.expiresAt, authMethod, isActive, config.ALERT_TIMES, alertLevel, forceLogout]);

  // Blocage accès si inactif
  if (!isActive) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-xl shadow-2xl text-center max-w-md mx-4">
          <div className="text-red-500 mb-6">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Session expirée</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Votre session a expiré. Vous allez être redirigé vers la page de connexion.
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  const hasValidSession = sessionInfo && (sessionInfo.user || Object.keys(sessionInfo).length > 0);

  if (!hasValidSession) {
    return null;
  }

  return (
    <>
      <ActivityTracker 
        onActivity={handleActivity}
        isActive={isActive}
        authMethod={authMethod}
      />

      {/* ✅ SingleTabEnforcer DÉSACTIVÉ - Le backend gère avec browserFingerprint */}
      {/* Le backend gère déjà la session unique par browserFingerprint */}

      {authMethod === 'session' && (
        <SessionAlerts
          alertLevel={alertLevel}
          timeLeft={timeLeft}
          sessionInfo={sessionInfo}
          onExtend={extendSession}
          onLogout={forceLogout}
          isActive={isActive}
        />
      )}

      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-2 rounded text-xs font-mono z-50">
          <div>Mode: {authMethod || 'None'}</div>
          <div>Session: {sessionInfo?.sessionId?.substr(0, 8) || 'JWT'}</div>
          <div>Temps: {Math.floor((timeLeft || 0) / (60 * 1000))}min</div>
          <div>Alerte: {alertLevel || 'None'}</div>
          <div>Extension: {sessionInfo?.canExtend ? 'Oui' : 'Non'}</div>
          <div>Activité: {Math.floor((Date.now() - lastActivity) / 1000)}s</div>
          <div>ExpiresAt: {sessionInfo?.expiresAt ? new Date(sessionInfo.expiresAt).toLocaleTimeString() : 'N/A'}</div>
        </div>
      )}
    </>
  );
};

export default SessionManager;
